import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { supabase } from '../../lib/supabaseClient';
import Link from 'next/link';

import {
  LayoutDashboard,
  Gamepad2,
  FolderTree,
  SearchX,
  Tags,
  FileText,
  Globe,
  RefreshCw,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export default function AdminLayout({ children }) {
  const router = useRouter();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // rebuild: 'idle' | 'loading' | 'success' | 'error'
  const [rebuildState, setRebuildState] = useState('idle');
  const [rebuildMsg, setRebuildMsg] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (!session && router.pathname !== '/admin/login') {
        router.push('/admin/login');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [router]);

  if (loading) {
    return <div className="min-h-screen bg-[#0f172a] flex items-center justify-center text-white">Loading Admin Panel...</div>;
  }

  // If not logged in and not on login page, render nothing while redirecting
  if (!session && router.pathname !== '/admin/login') {
    return null;
  }

  // If on login page, don't show the sidebar
  if (router.pathname === '/admin/login') {
    return <>{children}</>;
  }

  const navItems = [
    { name: 'Overview', href: '/admin', icon: LayoutDashboard },
    { name: 'Published Games', href: '/admin/games', icon: Gamepad2 },
    { name: 'Staging Board', href: '#', icon: FileText },
    { name: 'Categories', href: '/admin/categories', icon: FolderTree },
    { name: 'Missing Queries', href: '/admin/missing-queries', icon: SearchX },
    { name: 'Tag Pages', href: '#', icon: Tags },
    { name: 'Static Pages', href: '/admin/pages', icon: FileText },
    { name: 'Visit Website', href: '/', icon: Globe },
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.push('/admin/login');
  };

  const handleRebuild = async () => {
    if (rebuildState === 'loading') return;
    setRebuildState('loading');
    setRebuildMsg('');
    try {
      const res = await fetch('/api/rebuild', { method: 'POST' });
      const json = await res.json();
      if (res.ok) {
        setRebuildState('success');
        setRebuildMsg('Build triggered! (~2 min to go live)');
      } else {
        setRebuildState('error');
        setRebuildMsg(json.error || 'Failed to trigger rebuild.');
      }
    } catch (err) {
      setRebuildState('error');
      setRebuildMsg('Network error. Try again.');
    } finally {
      setTimeout(() => { setRebuildState('idle'); setRebuildMsg(''); }, 4000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-300 font-sans flex">
      {/* Sidebar */}
      <aside className="w-[260px] bg-[#172033] border-r border-slate-800 flex flex-col hidden md:flex sticky top-0 h-screen">
        <div className="h-16 flex items-center px-6">
          <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">
            Console
          </h1>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = router.pathname === item.href || (router.pathname.startsWith(item.href) && item.href !== '/admin' && item.href !== '/');
            return (
              <Link key={item.name} href={item.href}>
                <a className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-[#2a2a4a] text-indigo-300' : 'hover:bg-slate-800 hover:text-white'}`}>
                  <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-indigo-400' : 'text-slate-400'}`} />
                  {item.name}
                </a>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-4">
          <button
            onClick={handleRebuild}
            disabled={rebuildState === 'loading'}
            className={`w-full flex items-center justify-center font-semibold py-2 px-4 rounded-lg transition-colors text-white ${
              rebuildState === 'success' ? 'bg-green-600 cursor-default' :
              rebuildState === 'error'   ? 'bg-rose-600 cursor-default' :
              rebuildState === 'loading' ? 'bg-emerald-700 cursor-wait opacity-80' :
              'bg-emerald-500 hover:bg-emerald-600'
            }`}
          >
            {rebuildState === 'loading' && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
            {rebuildState === 'success' && <CheckCircle className="mr-2 h-4 w-4" />}
            {rebuildState === 'error'   && <XCircle className="mr-2 h-4 w-4" />}
            {rebuildState === 'idle'    && <RefreshCw className="mr-2 h-4 w-4" />}
            {rebuildState === 'loading' ? 'Triggering...' :
             rebuildState === 'success' ? 'Build Queued!' :
             rebuildState === 'error'   ? 'Failed' :
             'Rebuild Live Site'}
          </button>
          {rebuildMsg && (
            <p className={`text-xs text-center px-1 ${
              rebuildState === 'success' ? 'text-green-400' : 'text-rose-400'
            }`}>{rebuildMsg}</p>
          )}
          <div className="text-xs text-slate-500 text-center truncate px-2">
            {session?.user?.email}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center bg-slate-800 hover:bg-slate-700 text-rose-400 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Log Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
